import Dropdown from '@allesauto/core/dropdown';
import InfoIconBox from '@allesauto/core/infoIconBox';
import InputField from '@allesauto/core/inputField';
import NextLink from '@allesauto/core/nextLink';
import RadioButton from '@allesauto/core/radioButton';
import TextLink from '@allesauto/core/textLink';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  MUTATION_CALCULATE_LOAN,
  PaymentType,
  SchemaTypes,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { parseFormattedNumber } from '@allesauto/utils/funcs';
import { isWhiteLabel } from '@allesauto/utils/funcs.ts';
import { useMutation } from '@apollo/client';
import { CurrencyEuro16 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import validationSchema from '../validation/financingFormSchema';
import FooterPrice from '../../OverlayFormFooter/OverlayFooterPrice';
import Footer from '../../OverlayFormFooter/OverlayFormFooter';

export interface FinancingFormValues {
  downPayment: number;
  duration: number;
  mileage?: number;
  finalInstallment?: number;
  hasFinalInstallment?: boolean;
  monthlyInstallment: number;
}

export interface FinancingFormProps
  extends Pick<
    SchemaTypes.FinanceCalculationResult,
    'summary' | 'monthlyInstallment' | 'finalInstallment'
  > {
  options: SchemaTypes.LoanOptions;
  vehicleId: string;
  dealerId?: string;
  carConfiguratorId?: string;
  onSubmit?: (values: FinancingFormValues) => void;
  setCalculateLoanSummary:
    | React.Dispatch<React.SetStateAction<string>>
    | undefined;
}

interface FormValues {
  downPayment: string;
  duration: number;
  hasFinalInstallment: boolean;
  mileage: number;
  finalInstallment: string;
}

export const EuroSign = ({ disabled = false }: { disabled?: boolean }) => (
  <CurrencyEuro16
    className={clsx(
      'absolute bottom-[17px] right-20px',
      disabled ? 'text-grey50' : 'text-purple80 monochrome:text-grey90'
    )}
  />
);

export function arrayFromCalcParam({
  min,
  max,
  step,
}: SchemaTypes.FinanceCalculatorParameter) {
  step = step ?? 1;

  const values = [];
  let value = min;

  while (value < max) {
    values.push(value);

    value += step;
  }

  // always push the max value manually, cos sometimes (the second last value + step) !== max
  values.push(max);

  return values;
}

const FinancingForm = ({
  options: {
    downPayment,
    mileage,
    duration,
    finalInstallment,
    finalInstallmentToggle,
    legalText,
  },
  monthlyInstallment: monthlyInstallmentDefault,
  summary: summaryDefault,
  finalInstallment: finalInstallmentCalc,
  vehicleId,
  dealerId,
  carConfiguratorId,
  onSubmit,
  setCalculateLoanSummary,
}: FinancingFormProps) => {
  const intl = useIntl();
  const whiteLabel = useWhiteLabel();

  const [getResult, { data, loading, error, reset }] = useMutation<
    Pick<SchemaTypes.Mutation, 'calculateLoan'>
  >(MUTATION_CALCULATE_LOAN);

  const [monthlyInstallment, setMonthlyInstallment] = useState(
    monthlyInstallmentDefault
  );
  const [summary, setSummary] = useState(summaryDefault);

  // we only use this if the final installment is fixed (and the field is disabled)
  const [finalInstallmentFixed, setFinalInstallmentFixed] = useState(
    finalInstallmentCalc ?? 0
  );

  // update state after getting a new result from BE
  useEffect(() => {
    if (data?.calculateLoan) {
      setMonthlyInstallment(data.calculateLoan.monthlyInstallment);
      setSummary(data.calculateLoan.summary ?? '');
      if (setCalculateLoanSummary) {
        setCalculateLoanSummary(data.calculateLoan.summary ?? '');
      }

      // if finalInstallment is fixed (disabled), we update it with a calculated value
      if (!finalInstallment.isActive && data.calculateLoan.finalInstallment) {
        setFinalInstallmentFixed(data.calculateLoan.finalInstallment ?? 0);
      }
    }
  }, [data, finalInstallment]);

  // send all the form values plus the calculated values back to PDP
  const onFormikSubmit = useCallback(
    (formValues: FormValues) => {
      if (onSubmit && !loading && !error) {
        const finalInstallmentCustom = formValues.finalInstallment
          ? parseFormattedNumber(intl, formValues.finalInstallment)
          : finalInstallment.min;

        const values: FinancingFormValues = {
          downPayment: formValues.downPayment
            ? parseFormattedNumber(intl, formValues.downPayment)
            : downPayment.min,
          duration: formValues.duration,
          mileage: formValues.hasFinalInstallment
            ? formValues.mileage
            : undefined,
          finalInstallment: formValues.hasFinalInstallment
            ? finalInstallment.isActive
              ? finalInstallmentCustom
              : finalInstallmentFixed
            : undefined,
          hasFinalInstallment: formValues.hasFinalInstallment,
          monthlyInstallment,
        };

        onSubmit(values);
      }
    },
    [
      onSubmit,
      intl,
      loading,
      error,
      downPayment,
      finalInstallment,
      finalInstallmentFixed,
      monthlyInstallment,
    ]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      downPayment: intl.formatNumber(downPayment.default),
      duration: duration.default,
      hasFinalInstallment: finalInstallmentToggle.default,
      mileage: mileage.default,
      finalInstallment: intl.formatNumber(finalInstallment.default, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    },
    onSubmit: onFormikSubmit,
    validationSchema: useMemo(
      () =>
        validationSchema({
          messages: {
            number: translate(intl, 'ui.FORMS.VALIDATION_MESSAGES.NUMBER.TYPE'),
            integer: translate(
              intl,
              'ui.FORMS.VALIDATION_MESSAGES.NUMBER.INTEGER'
            ),
            min: translate(intl, 'ui.FORMS.VALIDATION_MESSAGES.NUMBER.MIN'),
            max: translate(intl, 'ui.FORMS.VALIDATION_MESSAGES.NUMBER.MAX'),
          },
          downPayment: {
            transform: (v) => parseFormattedNumber(intl, v),
            min: downPayment.min,
            max: downPayment.max,
          },
          finalInstallment: finalInstallment.isActive
            ? {
                transform: (v) => parseFormattedNumber(intl, v),
                min: finalInstallment.min,
                max: finalInstallment.max,
              }
            : undefined,
        }),
      [downPayment, finalInstallment, intl]
    ),
  });

  // make a calculation request to BE on each form values change
  const sendValues = useCallback(
    debounce(
      async (
        prevent: boolean,
        formValues: FormValues,
        downPaymentMin: number,
        finalInstallmentMin: number
      ) => {
        if (prevent) {
          return;
        }

        try {
          // todo: mileage is required on BE, but not in the design, that's why we always send it, it might be changed in the future
          const values = {
            ...formValues,
            downPayment: formValues.downPayment
              ? parseFormattedNumber(intl, formValues.downPayment)
              : downPaymentMin,
            finalInstallment: formValues.hasFinalInstallment
              ? formValues.finalInstallment
                ? parseFormattedNumber(intl, formValues.finalInstallment)
                : finalInstallmentMin
              : undefined,
          };

          await getResult({
            variables: {
              vehicleId,
              dealerId,
              carConfiguratorId,
              input: values,
            },
          });
        } catch (error) {
          // the error is handled by the apollo errorHandler
        }
      },
      500
    ),
    [vehicleId, getResult, intl, dealerId]
  );

  useEffect(() => {
    sendValues(
      formik.isValidating || !formik.isValid,
      formik.values,
      downPayment.min,
      finalInstallment.min
    );
    return () => {
      reset();
    };
  }, [
    formik.isValidating,
    formik.isValid,
    formik.values,
    downPayment,
    finalInstallment,
    sendValues,
    reset,
  ]);

  const mileageOptions = useMemo(
    () =>
      arrayFromCalcParam(mileage).map((value) => ({
        value,
        text: translate(intl, 'ui.PAYMENT_OPTIONS.MILEAGE_OPTION_TEXT', {
          value: intl.formatNumber(value),
        }),
      })),
    [mileage, intl]
  );

  const durationOptions = useMemo(
    () =>
      arrayFromCalcParam(duration).map((value) => ({
        value,
        text: translate(intl, 'ui.PAYMENT_OPTIONS.DURATION_OPTION_TEXT', {
          value: intl.formatNumber(value),
        }),
      })),
    [duration, intl]
  );

  const className = {
    topTextBlock: 'mb-40px md:mb-50px',
    topText: 'mb-20px md:mb-15px',
    form: 'mb-40px md:mb-30px',
    radios: 'mb-30px grid grid-cols-2 gap-20px md:gap-30px',
    fields: 'gap-15px md:gap-30px grid grid-cols-1 md:grid-cols-2',
    fieldLabel: 'flex items-center mb-5px',
    fieldLabelText: 'text-p1 mr-10px',
    accordion: 'border-t-1px border-t-grey30 mb-20px md:mb-30px',
    summaryText: 'mb-20px md:mb-15px text-p1',
    legalText: 'text-grey50 text-legal',
  };

  return (
    <>
      <div className={className.topTextBlock} data-hide-for-print="">
        <p className={className.topText}>
          {isWhiteLabel(whiteLabel)
            ? translate(intl, 'ui.PAYMENT_OPTIONS.FINANCING_FORM.TEXT_WL')
            : translate(intl, 'ui.PAYMENT_OPTIONS.FINANCING_FORM.TEXT')}
        </p>
        {!whiteLabel && (
          <NextLink href="/pages/autokauf-bezahlung" passHref legacyBehavior>
            <TextLink
              text={translate(intl, 'ui.PAYMENT_OPTIONS.FINANCING_FORM.LINK')}
            />
          </NextLink>
        )}
      </div>

      <form className={className.form} onSubmit={(e) => e.preventDefault()}>
        <div className={className.radios}>
          <RadioButton
            label={translate(
              intl,
              'ui.PAYMENT_OPTIONS.FINANCING_FORM.WITH_FINAL_INSTALLMENT'
            )}
            checked={formik.values.hasFinalInstallment}
            onChange={() => formik.setFieldValue('hasFinalInstallment', true)}
            disabled={!finalInstallmentToggle.isActive}
          />
          <RadioButton
            label={translate(
              intl,
              'ui.PAYMENT_OPTIONS.FINANCING_FORM.WITHOUT_INSTALLMENT'
            )}
            checked={!formik.values.hasFinalInstallment}
            onChange={() => {
              formik.setFieldValue('hasFinalInstallment', false);

              if (formik.errors.finalInstallment) {
                formik.setFieldValue(
                  'finalInstallment',
                  intl.formatNumber(finalInstallment.default, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                );
              }
            }}
            disabled={!finalInstallmentToggle.isActive}
          />
        </div>

        <div className={className.fields}>
          <div>
            <div className={className.fieldLabel}>
              <div className={className.fieldLabelText}>
                {translate(intl, 'ui.PAYMENT_OPTIONS.DOWN_PAYMENT_LABEL')}
              </div>
              <InfoIconBox
                text={translate(intl, 'ui.PAYMENT_OPTIONS.DOWN_PAYMENT_INFO')}
              />
            </div>
            <InputField
              placeholder={`${intl.formatNumber(downPayment.min)}`}
              value={formik.values.downPayment}
              onChange={({ currentTarget: { value } }) =>
                formik.setFieldValue('downPayment', value)
              }
              onBlur={(e) => {
                formik.handleBlur(e);

                if (!formik.errors.downPayment && e.currentTarget.value) {
                  formik.setFieldValue(
                    'downPayment',
                    intl.formatNumber(
                      parseFormattedNumber(intl, e.currentTarget.value)
                    )
                  );
                }
              }}
              errorMessage={formik.errors.downPayment}
              icon={<EuroSign />}
            />
          </div>

          <div>
            <div className={className.fieldLabel}>
              <div className={className.fieldLabelText}>
                {translate(intl, 'ui.PAYMENT_OPTIONS.DURATION_LABEL')}
              </div>
              <InfoIconBox
                text={translate(intl, 'ui.PAYMENT_OPTIONS.DURATION_INFO')}
              />
            </div>
            <Dropdown
              context="financing-form"
              items={durationOptions}
              value={formik.values.duration}
              onChange={({ value }) => formik.setFieldValue('duration', value)}
            />
          </div>

          {formik.values.hasFinalInstallment && (
            <>
              <div>
                <div className={className.fieldLabel}>
                  <div className={className.fieldLabelText}>
                    {translate(intl, 'ui.PAYMENT_OPTIONS.MILEAGE_LABEL')}
                  </div>
                  <InfoIconBox
                    text={translate(intl, 'ui.PAYMENT_OPTIONS.MILEAGE_INFO')}
                  />
                </div>
                <Dropdown
                  context="financing-form"
                  items={mileageOptions}
                  value={formik.values.mileage}
                  onChange={({ value }) =>
                    formik.setFieldValue('mileage', value)
                  }
                />
              </div>

              <div>
                <div className={className.fieldLabel}>
                  <div className={className.fieldLabelText}>
                    {translate(
                      intl,
                      'ui.PAYMENT_OPTIONS.FINANCING_FORM.FINAL_INSTALLMENT_LABEL'
                    )}
                  </div>
                  <InfoIconBox
                    text={translate(
                      intl,
                      'ui.PAYMENT_OPTIONS.FINANCING_FORM.FINAL_INSTALLMENT_INFO'
                    )}
                  />
                </div>
                <InputField
                  placeholder={`${intl.formatNumber(finalInstallment.min, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                  value={
                    finalInstallment.isActive
                      ? formik.values.finalInstallment
                      : intl.formatNumber(finalInstallmentFixed, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                  }
                  onChange={({ currentTarget: { value } }) => {
                    formik.setFieldValue('finalInstallment', value);
                  }}
                  onBlur={(e) => {
                    formik.handleBlur(e);

                    if (
                      !formik.errors.finalInstallment &&
                      e.currentTarget.value
                    ) {
                      formik.setFieldValue(
                        'finalInstallment',
                        intl.formatNumber(
                          parseFormattedNumber(intl, e.currentTarget.value),
                          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                        )
                      );
                    }
                  }}
                  errorMessage={formik.errors.finalInstallment}
                  icon={<EuroSign disabled={!finalInstallment.isActive} />}
                  disabled={!finalInstallment.isActive}
                />
              </div>
            </>
          )}
        </div>
      </form>

      {/* <Accordion
        isNarrow
        className={className.accordion}
        items={[
          {
            id: 'creditAssessment',
            title: translate(
              intl,
              'ui.PAYMENT_OPTIONS.CREDIT_ASSESSMENT_TITLE'
            ),
            children: 'Not implemented',
          },
        ]}
      /> */}

      <p
        className={clsx(className.summaryText, loading && 'opacity-30')}
        dangerouslySetInnerHTML={{ __html: summary ?? '' }}
      />

      <p className={className.legalText}>{legalText.replace('* ', '¹ ')}</p>
      <div data-hide-for-print="">
        <Footer
          buttonText={translate(intl, 'ui.PAYMENT_OPTIONS.BUTTON_TEXT')}
          onButtonClick={formik.submitForm}
        >
          <FooterPrice
            paymentType={PaymentType.LoanRateRange}
            amount={monthlyInstallment}
          />
        </Footer>
      </div>
    </>
  );
};

export default FinancingForm;
